import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  BY_HOURS_LABEL,
  NOT_CREDITED_REASON_LABEL,
  NOT_TIED_TO_A_JOB_LABEL,
  NO_SERVICE_TYPE,
  NO_SERVICE_TYPE_LABEL,
  SPLIT_EVENLY_LABEL,
  can,
  drillShareWords,
  rangeLabel,
  revenueMovementKey,
  type RevenueBasis,
  type RevenueFigure,
  type RevenueDrillAnswer,
  type RevenueInvoiceRow,
  type RevenueMovementRow,
  type RevenuePage as RevenuePagePayload,
  type Role,
} from '@servwell/shared';
import { useTRPC } from '../../../lib/trpc';
import { useSession } from '../../session';
import { JOB_PAGES_ROLES, mayEnter } from '../../nav';
import { Sheet } from '../../ui/Sheet';
import { Notice } from '../../ui/Badge';
import { Button, ghostButton } from '../../ui/Button';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
  unresolved,
} from '../../ui/QueryStates';
import { Skeleton } from '../../ui/Skeleton';
import { IconReports } from '../../icons';
import { PAYMENT_METHOD_LABEL, money } from '../../money';
import { BASIS_WORDS } from './basisWords';

/**
 * The Revenue page's drill (ops insights Phase 2, ticket 06): any figure of the period opens
 * the invoices behind it in the side panel, and they add up to it; under Collected (ticket 07)
 * the payments and refunds behind it. The server decides the rows (`report.revenueDrill`,
 * through the same pieces as the page) and how many there are; this draws them.
 *
 * The panel is the kit's `Sheet`, which traps focus while open and hands it back to the figure
 * that opened it. The open drill is not in the address (spec, Out of Scope): the page holds it.
 *
 * Each row: its invoice (a link, `/invoices/$invoiceId`, which every role that reads this page
 * may open), its issue day in the shop's zone, the customer, its job and service type, and the
 * amount this figure counts of it. A person's row says how their share was decided; a "Not
 * credited" row says why nobody was. The job is a link only for the roles its page admits
 * (`JOB_PAGES_ROLES`); for anyone else it is its number and title.
 *
 * Under Collected each row is a payment or a refund: the shop's day it happened, how it was
 * paid, its invoice (a link, when it is the shop's own), the customer, the job and the type, and
 * the amount with its sign; a person's row is their share of it, and says how it was decided.
 *
 * A figure with more rows than the server sends says so, and that the figure counts all of
 * them. "No service type" offers those who may classify jobs the Jobs list, filtered to them.
 *
 * The panel states the figure the server added up, not the one the page drew: the two are read
 * apart, and an invoice issued or a job reclassified in between moves one and not the other.
 * When they differ, the panel says so and offers to read both again.
 */

/** A figure the page opened, and what the panel is titled: the page's own words for it. */
export interface Drill {
  figure: RevenueFigure;
  title: string;
}

/**
 * A figure of the period opens the invoices behind it when it holds anything in the period: an
 * amount, or something invoiced for nothing (`count`: a type row's jobs, a person's jobs, "Not
 * credited"'s invoices). A row there only for a year earlier holds neither, and a year earlier
 * does not drill.
 */
export const opensInPeriod = (cents: number, count: number | null) =>
  cents !== 0 || (count ?? 0) > 0;

/**
 * A figure as the page drew it: what the drill's own figure should be. A row the page does not
 * have (a type reclassified away since, say) is 0 there. Picked out, never added up.
 */
export function figureCents(page: RevenuePagePayload, figure: RevenueFigure): number {
  switch (figure.kind) {
    case 'total':
      return page.kpis.total.cents;
    case 'month':
      return page.trend.find((m) => m.month === figure.month)?.cents ?? 0;
    case 'service-type':
      return page.byServiceType.find((r) => r.key === figure.key)?.cents ?? 0;
    case 'person':
      return page.byPerson?.rows.find((r) => r.userId === figure.userId)?.cents ?? 0;
    case 'not-credited':
      return page.byPerson?.notCredited.cents ?? 0;
  }
}

/** The side panel, open while there is a drill. */
export function RevenueDrill({
  drill,
  pageCents,
  range,
  basis,
  onClose,
}: {
  drill: Drill | null;
  /** The opened figure as the page has it now (`figureCents`), or null before it has loaded. */
  pageCents: number | null;
  /** The page's period: the days a figure other than a month is of. */
  range: { from: string; to: string };
  basis: RevenueBasis;
  onClose: () => void;
}) {
  return (
    <Sheet open={drill !== null} onClose={onClose} title={drill?.title ?? ''}>
      {drill ? (
        <DrillBody
          figure={drill.figure}
          pageCents={pageCents}
          range={range}
          basis={basis}
        />
      ) : null}
    </Sheet>
  );
}

/**
 * Read the Revenue page and its drills again (`report.revenue`, `report.revenueDrill`, any
 * period), and nothing wider: what a panel whose figure has changed since the page loaded asks
 * for.
 */
export function refreshRevenue(
  queryClient: QueryClient,
  trpc: ReturnType<typeof useTRPC>,
): void {
  void queryClient.invalidateQueries({ queryKey: trpc.report.revenue.pathKey() });
  void queryClient.invalidateQueries({ queryKey: trpc.report.revenueDrill.pathKey() });
}

/** The panel's body: the read, and its states. Exported for a test over a seeded cache. */
export function DrillBody({
  figure,
  pageCents,
  range,
  basis,
}: {
  figure: RevenueFigure;
  pageCents: number | null;
  range: { from: string; to: string };
  basis: RevenueBasis;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { role } = useSession();
  const drill = useQuery(
    trpc.report.revenueDrill.queryOptions({ ...range, basis, figure }),
  );
  const phase = shownState(drill, drill.data);
  return (
    <div className="space-y-4">
      <StaleNotice
        show={refreshFailed(drill, drill.data)}
        onRetry={() => void drill.refetch()}
      />
      {phase === 'ready' && drill.data ? (
        <DrillAnswer
          answer={drill.data}
          figure={figure}
          role={role}
          pageCents={pageCents}
          onRefresh={() => refreshRevenue(queryClient, trpc)}
        />
      ) : phase === 'loading' ? (
        <DrillSkeleton />
      ) : (
        <UnresolvedState
          state={unresolved(phase)}
          what={`these ${BASIS_WORDS[basis].rows}`}
          icon={IconReports}
          error={drill.error}
          onRetry={() => void drill.refetch()}
        />
      )}
    </div>
  );
}

/** Whether `role` may open a job's page: the roles the job pages admit. */
const opensJobs = (role: Role) => mayEnter(role, JOB_PAGES_ROLES);

/**
 * Whether `role` is offered "Classify these jobs": the Jobs list is a page it may open, and
 * setting a job's type is something it may do there.
 */
export const mayClassify = (role: Role) => opensJobs(role) && can(role, 'job', 'update');

/**
 * Said when the figure the server just added up is not the one the page drew: the page was
 * read before an invoice was issued or voided, or a job reclassified. Refresh reads the page and
 * its drills again. No hooks, so a test can press it.
 */
export function FigureChanged({ onRefresh }: { onRefresh: () => void }) {
  return (
    <Notice tone="warn">
      <span className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span>This figure has changed since the page loaded.</span>
        <Button variant="ghost" onClick={onRefresh}>
          Refresh
        </Button>
      </span>
    </Notice>
  );
}

/**
 * The server's answer, drawn for `role`: the figure, how many invoices (or payments and
 * refunds) and over which days, a notice when it is no longer the page's figure, the cap line
 * when fewer were sent than there are, "Classify these jobs" on "No service type" for those who
 * may, and the rows. No hooks; the links need a router above it.
 */
export function DrillAnswer({
  answer,
  figure,
  role,
  pageCents,
  onRefresh,
}: {
  answer: RevenueDrillAnswer;
  figure: RevenueFigure;
  role: Role;
  /** The figure as the page has it now, or null before the page has loaded. */
  pageCents: number | null;
  onRefresh: () => void;
}) {
  const days = rangeLabel(answer.range.from, answer.range.to);
  const words = BASIS_WORDS[answer.basis];
  const classify =
    figure.kind === 'service-type' && figure.key === NO_SERVICE_TYPE && mayClassify(role);
  return (
    <>
      {pageCents !== null && pageCents !== answer.cents ? (
        <FigureChanged onRefresh={onRefresh} />
      ) : null}
      <div>
        <p className="font-heading text-2xl font-bold tabular-nums">
          {money(answer.cents)}
        </p>
        <p className="text-sm text-muted-foreground">
          {words.rowCount(answer.count)} · {days}
        </p>
      </div>
      {answer.count > answer.rows.length ? (
        <Notice tone="info">
          Showing {answer.rows.length.toLocaleString('en-US')} of{' '}
          {answer.count.toLocaleString('en-US')}. The figure above counts all of them.
        </Notice>
      ) : null}
      {classify ? (
        <Link
          to="/jobs"
          search={{ serviceType: NO_SERVICE_TYPE }}
          className={`${ghostButton} w-full`}
        >
          Classify these jobs
        </Link>
      ) : null}
      {answer.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {`${words.noRows} behind this figure in ${days}.`}
        </p>
      ) : (
        <ul className="min-w-0 divide-y divide-border rounded-[var(--radius)] border border-border bg-surface">
          {answer.basis === 'collected'
            ? answer.rows.map((row) => (
                <MovementRow
                  key={revenueMovementKey(row)}
                  row={row}
                  linksJob={opensJobs(role)}
                />
              ))
            : answer.rows.map((row) => (
                <InvoiceRow key={row.invoiceId} row={row} linksJob={opensJobs(role)} />
              ))}
        </ul>
      )}
    </>
  );
}

/** A link to an invoice's page, which every role that reads this page may open. */
function InvoiceLink({ id, number }: { id: string; number: number | null }) {
  return (
    <Link
      to="/invoices/$invoiceId"
      params={{ invoiceId: id }}
      className="font-semibold text-[var(--sw-brand-text)] hover:underline"
    >
      Invoice #{number ?? '—'}
    </Link>
  );
}

/** One invoice: what it is, whose, on which job, and what this figure counts of it. */
function InvoiceRow({ row, linksJob }: { row: RevenueInvoiceRow; linksJob: boolean }) {
  return (
    <li data-row="invoice" className="min-w-0 space-y-1 px-4 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <InvoiceLink id={row.invoiceId} number={row.invoiceNumber} />
        <span className="shrink-0 font-semibold tabular-nums">{money(row.cents)}</span>
      </div>
      <p className="text-xs text-muted-foreground wrap-anywhere">
        {rangeLabel(row.issuedOn, row.issuedOn)} · {row.customer ?? '—'}
      </p>
      <WhereItLanded row={row} linksJob={linksJob} />
    </li>
  );
}

/**
 * One payment or refund: which, the day, how it was paid, whose, the invoice it paid (a link
 * when it is the shop's own; "No invoice of this shop's" otherwise), its job and type, and what
 * this figure counts of it, with its sign.
 */
function MovementRow({ row, linksJob }: { row: RevenueMovementRow; linksJob: boolean }) {
  return (
    <li data-row={row.kind} className="min-w-0 space-y-1 px-4 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-semibold">
          {row.kind === 'refund' ? 'Refund' : 'Payment'}
        </span>
        <span className="shrink-0 font-semibold tabular-nums">{money(row.cents)}</span>
      </div>
      <p className="text-xs text-muted-foreground wrap-anywhere">
        {rangeLabel(row.movedOn, row.movedOn)} ·{' '}
        {PAYMENT_METHOD_LABEL[row.method] ?? row.method} · {row.customer ?? '—'}
      </p>
      <p className="text-xs wrap-anywhere">
        {row.invoice ? (
          <InvoiceLink id={row.invoice.id} number={row.invoice.number} />
        ) : (
          <span className="text-muted-foreground">No invoice of this shop’s</span>
        )}
      </p>
      <WhereItLanded row={row} linksJob={linksJob} />
    </li>
  );
}

/**
 * A row's job and type (or "Not tied to a job"), and for a person's figure how their share was
 * decided, or for "Not credited" why nobody was credited: the same lines under an invoice and
 * under a payment or refund.
 */
function WhereItLanded({
  row,
  linksJob,
}: {
  row: Pick<RevenueInvoiceRow, 'job' | 'serviceType' | 'share' | 'reason'>;
  linksJob: boolean;
}) {
  // "Job #9503", never a bare "#9503": the row's invoice is "Invoice #9501", and another
  // invoice in the same period can be #9503 (Phase 2 QA, D5).
  const jobName = row.job ? `Job #${row.job.number} ${row.job.title}` : null;
  return (
    <>
      <p className="text-xs text-muted-foreground wrap-anywhere">
        {row.job === null ? (
          NOT_TIED_TO_A_JOB_LABEL
        ) : (
          <>
            {linksJob ? (
              <Link
                to="/jobs/$jobId"
                params={{ jobId: row.job.id }}
                className="text-[var(--sw-brand-text)] hover:underline"
              >
                {jobName}
              </Link>
            ) : (
              jobName
            )}{' '}
            · {row.serviceType ?? NO_SERVICE_TYPE_LABEL}
          </>
        )}
      </p>
      {row.share ? (
        <p className="text-xs">
          {row.share.rule === 'by-hours' ? BY_HOURS_LABEL : SPLIT_EVENLY_LABEL}:{' '}
          {drillShareWords(row.share)}
        </p>
      ) : null}
      {row.reason ? (
        <p className="text-xs">
          Not credited: {NOT_CREDITED_REASON_LABEL[row.reason].toLowerCase()}
        </p>
      ) : null}
    </>
  );
}

/** The panel's shape while it loads: the figure, then rows. */
function DrillSkeleton() {
  return (
    <div className="space-y-3" aria-busy="true">
      <Skeleton className="h-8 w-32" />
      <Skeleton className="h-4 w-48" />
      <Skeleton className="h-20" />
      <Skeleton className="h-20" />
    </div>
  );
}
